import React, { useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useAuthContext } from '../../../context/auth.context'
import { lmsAdmin } from '../api'
import { accessLevelLabel } from '../format'
import { FAILED_STATE, LmsGrid, NameCell, PhoneLine, useGridRows } from '../lms-grid'
import { LmsIcon } from '../lms-icon'
import { Pill } from '../../../shared/sso-workspace/pill'
import { useGridList } from '../../../shared/sso-workspace/fleet-grid'
import { useIsMobile } from '../../../shared/hooks/use-is-mobile'
import { TableAgClient } from '../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnText } from '../../../shared/table-builder/table-columns'
import { IconCourse } from '../../../shared/icon/icon'
import '../lms-admin.scss'
import { LmsTrail, lmsTrail } from '../lms-trail'
import { SECTION } from '../../../shared/navigation/sections'

// Courses (docs/08 §16, design edxr-courses-v1.0.html). v1 shows the
// LMS-integrated catalog: course mappings aggregated across connections
// with "published to N connections · M packages" badges, each row opening
// the Course → LMS Integration page. The full org course catalog lives in
// the content service — not duplicated here; any course can be opened
// directly by node id (deviations D-049).

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

// The LMS-integrated courses: every connection that is not archived is asked
// for its mappings, and they are folded into one row per course. A connection
// that cannot answer is not "no courses": when none can, this throws (the page
// says the courses could not load); when some can, the rows come with a
// warning that names how many did not.
const readCourses = async () => {
  const { connections = [] } = await lmsAdmin.listConnections()
  const usable = connections.filter(c => c.status !== 'archived')
  const answers = await Promise.all(
    usable.map(connection =>
      lmsAdmin
        .listConnectionCourses(connection.id)
        .then(result => ({ connection, mappings: result.courses || [] }))
        .catch(failure => ({ connection, failure }))
    )
  )
  const failed = answers.filter(answer => answer.failure)
  if (usable.length && failed.length === usable.length) {
    const reason = failed[0].failure.message
    const what = usable.length === 1 ? 'the connection could not be read' : `none of the ${usable.length} connections could be read`
    throw new Error(`${what}${reason ? ` (${reason})` : ''}.`)
  }
  const byNode = {}
  answers
    .filter(answer => !answer.failure)
    .forEach(({ connection, mappings }) => {
      mappings.forEach(mapping => {
        const entry = (byNode[mapping.node_id] = byNode[mapping.node_id] || {
          nodeId: mapping.node_id,
          name: mapping.node_name || `course ${mapping.node_id}`,
          accessLevel: mapping.node_access_level,
          connections: [],
          packages: 0
        })
        entry.connections.push(connection.name)
        if (mapping.current_package_id) entry.packages += 1
      })
    })
  const names = failed.map(answer => answer.connection.name)
  const shown = names.length > 3 ? `${names.slice(0, 3).join(', ')} and ${names.length - 3} more` : names.join(', ')
  return {
    rows: Object.values(byNode).sort((a, b) => a.name.localeCompare(b.name)),
    warning: failed.length ? `${failed.length} of ${usable.length} connections couldn't be read (${shown}), so their courses are missing here.` : null
  }
}

export const ViewCourses = () => {
  const history = useHistory()
  const authContext = useAuthContext()

  const [openNodeId, updateOpenNodeId] = useState('')
  const tableRef = useRef()
  const mobile = useIsMobile()

  // The grid's ready (and its footer's reload) is the loader (useGridRows). The
  // courses are read per connection, so a read can be partly there: courses
  // from the connections that answered show, and a warning names those that
  // did not. When none answered it is a failure.
  const { rows: courses, error, warning, ready } = useGridRows(readCourses)

  // The cells' words, flattened onto the row: the grid sorts, quick-filters
  // and exports the cell VALUE, so each column's field holds what it shows.
  const gridRows = useMemo(
    () =>
      Array.isArray(courses)
        ? courses.map(course => ({
          ...course,
          id: course.nodeId,
          visibility: course.accessLevel ? accessLevelLabel(course.accessLevel) : '',
          connectionCount: course.connections.length,
          phone: mobile,
          publishedText: `${plural(course.connections.length, 'connection')} · ${plural(course.packages, 'package')}`
        }))
        : courses,
    [courses, mobile]
  )
  const gridList = useGridList(gridRows)

  const customer = authContext.state.organizationName

  return (
    <div className="lms-admin" data-testid="lms-courses">
      <LmsTrail trail={lmsTrail(SECTION.lmsCourses)} />
      <div className="page-header">
        <div className="page-header-text">
          <h1 className="page-header-title">
            Courses
            <span className="page-header-customer">
              <LmsIcon name="building" />
              {customer}
            </span>
          </h1>
          <p className="page-header-sub">
            Courses published to this organization's LMS connections. Open any course by node id to add its first
            integration.
          </p>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {`Couldn't load courses: ${error.message}`}
        </div>
      )}
      {warning && (
        <div className="alert alert-warning" role="alert" data-testid="courses-partial">
          {warning}
        </div>
      )}

      <div className="filter-strip filter-strip-alone">
        <div className="spacer" />
        <input
          className="form-control form-control-sm"
          style={{ width: 140 }}
          type="number"
          min="1"
          placeholder="Node id…"
          value={openNodeId}
          data-testid="open-node-input"
          onChange={e => updateOpenNodeId(e.target.value)}
        />
        <button
          className="btn btn-light border btn-sm"
          type="button"
          disabled={!Number(openNodeId)}
          data-testid="open-node"
          onClick={() => history.push(`/org/lms/courses/${openNodeId}/integration`)}
        >
          Open integration
        </button>
      </div>

      <LmsGrid flush title="courses" tableRef={tableRef} exportName="lms-courses">
        <TableAgClient
          ref={tableRef}
          apiList={gridList}
          onReady={ready}
          sortCol="name"
          rowHeight={mobile ? ROW_HEIGHT_TWO_LINE : undefined}
          onRowClicked={course => history.push(`/org/lms/courses/${course.nodeId}/integration`)}
          emptyState={courses === null || (warning && courses.length === 0) ? FAILED_STATE : {
            Icon: IconCourse,
            title: 'No courses published to an LMS yet',
            message: "Map a course to a connection from its LMS Integration page — enter the course's node id above to open it directly."
          }}
          noMatchState={{ message: 'No courses match. Clear the search.' }}>
          <ColumnFormat field="name" header="Course" render={courseCell} stacked minWidth={mobile ? 150 : 220} flex={2} sortable />
          <ColumnText field="nodeId" header="Node" sortable hide={mobile} />
          <ColumnFormat field="visibility" header="Visibility" render={visibilityCell} sortable hide={mobile} />
          <ColumnFormat
            field="connectionCount"
            header="LMS integration"
            render={integrationCell}
            exportValue={course => course.publishedText}
            sortable
            minWidth={260}
            hide={mobile}
          />
        </TableAgClient>
      </LmsGrid>
    </div>
  )
}

// The course's name, a link to its LMS integration page; on a phone also its
// visibility and what it is published to, in place of the columns a phone has
// no room for.
const courseCell = cell => {
  const course = cell.data
  if (!course) return ''
  const phone = course.phone
    ? <PhoneLine pill={course.visibility ? <Pill>{course.visibility}</Pill> : null}>{course.publishedText}</PhoneLine>
    : null
  return <NameCell name={course.name} to={`/org/lms/courses/${course.nodeId}/integration`} phone={phone} />
}

const visibilityCell = cell =>
  cell.data ? (cell.data.visibility ? <Pill>{cell.data.visibility}</Pill> : <span className="sw-hint">—</span>) : ''

// How many connections the course is published to, and with how many packages.
const integrationCell = cell =>
  cell.data ? (
    <span className="d-inline-flex gap-1">
      <Pill tone="ok">{plural(cell.data.connections.length, 'connection')}</Pill>
      <Pill tone="info">{plural(cell.data.packages, 'package')}</Pill>
    </span>
  ) : ''
